import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import { Component, effect, inject, Injector, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxAutoFocusDirective,
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxModalComponent,
  OnyxRadioButtonComponent,
  OnyxFormGroupComponent,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  OnyxToastService,
  OnyxToggleComponent,
} from '@onyx/angular';
import { difference } from 'lodash';
import { Subject, switchMap } from 'rxjs';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { GoodsSizeType } from '../../../../../../common/enums/goods-size-type';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import { DictionariesNamesTranslationFormComponent } from '../../../common/components/forms/dictionaries-names-translation-form/dictionaries-names-translation-form.component';
import { CompanyDictionaryCode } from '../../../common/enums/company-dictionary-code';
import { DictionariesHelper } from '../../../common/helpers/dictionaries.helper';
import type { CompanyUnit } from '../../../common/interfaces/company-unit';
import { CompanyDictionariesService } from '../../../common/services/company-dictionaries.service';
import { UnitSuggestionsPipe } from '../../../../../../common/pipes/unit-suggestions.pipe';
import { UnitWarningPipe } from '../../../../../../common/pipes/unit-warning.pipe';

export type DictionariesUnitForm = ReturnType<
  ReturnType<DictionariesUnitModalComponent['buildForm']>['getRawValue']
>;

export type DictionariesUnitModalData =
  | {
      mode: OnyxFormMode.ADD;
      name?: string;
    }
  | {
      mode: OnyxFormMode.EDIT;
      unit: CompanyUnit;
    };

@Component({
  selector: 'app-dictionaries-unit-modal',
  imports: [
    UnitWarningPipe,
    UnitSuggestionsPipe,
    OnyxFormGroupComponent,
    OnyxSuggestionsComponent,
    OnyxModalComponent,
    ReactiveFormsModule,
    DictionariesNamesTranslationFormComponent,
    OnyxTextFieldComponent,
    OnyxRadioButtonComponent,
    OnyxButtonComponent,
    TranslatePipe,
    AsyncPipe,
    OnyxToggleComponent,
    OnyxAutoFocusDirective,
  ],
  templateUrl: './dictionaries-unit-modal.component.html',
  styleUrl: './dictionaries-unit-modal.component.scss',
})
export class DictionariesUnitModalComponent implements OnInit {
  protected readonly data = inject<DictionariesUnitModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<void>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly injector = inject(Injector);
  private readonly companyDictionariesService = inject(
    CompanyDictionariesService,
  );
  private readonly dictionariesService = inject(DictionariesService);
  private readonly dictionariesHelper = inject(DictionariesHelper);

  protected readonly I18N = 'dictionaries';

  protected readonly DictionaryCode = DictionaryCode;
  protected readonly OnyxFormMode = OnyxFormMode;
  protected readonly GoodsSizeType = GoodsSizeType;

  protected readonly sizeTypes$ = this.dictionariesService.getDictionary(
    DictionaryCode.GOODS_SIZE_TYPE,
  );

  protected form = this.buildForm();
  protected loading = signal(false);
  protected close$ = new Subject<void>();

  protected typeControl = this.form.controls.size.controls.type;
  protected type = toSignal(this.typeControl.valueChanges, {
    initialValue: this.typeControl.value,
  });

  public ngOnInit(): void {
    if (this.data.mode === OnyxFormMode.EDIT) {
      this.form.patchValue(this.data.unit);
    }

    const controls = this.form.controls;
    const sizeControls = controls.size.controls;

    const sizeTypeControls = {
      [GoodsSizeType.DIMENSIONS]: [
        sizeControls.length,
        sizeControls.height,
        sizeControls.width,
      ],
      [GoodsSizeType.LOADING_METERS]: [
        sizeControls.loadingMeters,
        sizeControls.requiresCoilWell,
      ],
      [GoodsSizeType.VOLUME]: [sizeControls.volume],
    };

    effect(
      () => {
        const type = this.type();
        const enabledControls = [
          sizeControls.type,
          ...(type ? sizeTypeControls[type] : []),
        ];
        const disabledControls = difference(
          Object.values(sizeControls),
          enabledControls,
        );

        ValidationHelper.enableControls(enabledControls);
        ValidationHelper.disableControls(disabledControls, { reset: true });
      },
      { injector: this.injector },
    );
  }

  protected async submit(): Promise<void> {
    if (!(await ValidationHelper.checkValidity(this.form, this.injector))) {
      return;
    }

    const form = this.form.getRawValue();

    let action$;
    switch (this.data.mode) {
      case OnyxFormMode.ADD:
        action$ = this.companyDictionariesService.addDictionaryItem(
          CompanyDictionaryCode.UNIT,
          form,
        );
        break;

      case OnyxFormMode.EDIT:
        {
          const { unit } = this.data;
          action$ = this.dictionariesHelper
            .confirmEdit(CompanyDictionaryCode.UNIT, unit, this.close$)
            .pipe(
              switchMap(() =>
                this.companyDictionariesService.editDictionaryItem(
                  unit.uuid,
                  form,
                ),
              ),
            );
        }
        break;
    }

    this.loading.set(true);
    action$
      .subscribe({
        next: () => {
          this.toastService.showSuccess({
            message:
              this.data.mode === OnyxFormMode.ADD
                ? `${this.I18N}.dictionaryAdded`
                : `${this.I18N}.dictionaryEdited`,
          });
          this.close$.next();
        },
        error: (response) =>
          this.dictionariesHelper.handleEditError(
            response,
            CompanyDictionaryCode.UNIT,
            form,
          ),
      })
      .add(() => this.loading.set(false));
  }

  private buildForm() {
    return this.fb.group({
      names: DictionariesNamesTranslationFormComponent.buildFom(this.fb),
      size: this.fb.group({
        type: this.fb.control(GoodsSizeType.LOADING_METERS, [
          Validators.required,
        ]),
        loadingMeters: this.fb.control<number | null>(null, [
          Validators.required,
        ]),
        requiresCoilWell: this.fb.control<boolean | null>(false, [
          Validators.required,
        ]),
        length: this.fb.control<number | null>(null, [Validators.required]),
        width: this.fb.control<number | null>(null, [Validators.required]),
        height: this.fb.control<number | null>(null, [Validators.required]),
        volume: this.fb.control<number | null>(null, [Validators.required]),
      }),
    });
  }
}
